import { ButtonGroup, Chip, cn, Tooltip } from '@heroui/react'
import { keepPreviousData, useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import CopyButton from 'components/Hero/CopyButton'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { Button } from 'components/ui/Button'
import { formatDistanceToNow } from 'date-fns'
import { OctagonX, Plus, Rows4 } from 'lucide-react'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import LogStream from './LogStream'

const statusMap = {
  initializing: { label: 'Initializing', color: 'default' },
  validating: { label: 'Validating', color: 'warning' },
  dispatching: { label: 'Dispatching', color: 'secondary' },
  in_progress: { label: 'In Progress', color: 'primary' },
  completed: { label: 'Completed', color: 'success' },
  completed_partial: { label: 'Completed Partial', color: 'success' },
  failed: { label: 'Failed', color: 'danger' },
}

export default function Batches() {
  const navigate = useNavigate()
  const take = 10

  const [rowSelection, setRowSelection] = useState({})
  const [activeBatchIds, setActiveBatchIds] = useState([])

  const { data, isLoading, isFetchingNextPage, refetch, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['batches', take],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await $fetch('/v2/batches/list', {
        query: {
          skip: pageParam * take,
          take,
        },
      })
      return data
    },
    refetchInterval: activeBatchIds?.length > 0 ? 5000 : false,
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage?.length === 0) {
        return undefined
      }
      return lastPageParam + 1
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined
      }
      return firstPageParam - 1
    },
  })

  const batches = useMemo(() => data?.pages?.flatMap(page => page) ?? [], [data])

  // Set active batches and row selection when the data is loaded
  useEffect(() => {
    if (batches?.length) {
      const isActive = batch => typeof batch.batch_status === 'string' && !['completed', 'failed'].includes(batch.batch_status)
      const activeIds = batches?.filter(isActive)?.map(batch => batch.id)
      setActiveBatchIds(activeIds)
      setRowSelection(prev => activeIds.reduce((acc, batchId) => ({ ...acc, [batchId]: true }), prev))
    }
  }, [batches])

  const stopBatch = useMutation({
    mutationFn: batchId => $fetch(`/v2/batches/${batchId}/stop`, {
      method: 'POST',
    }),
    onSuccess: () => {
      toast.success(`Successfully stopped remaining calls`)
      refetch()
    },
    onError: (error) => {
      console.error(error)
      toast.error('Failed to stop batch')
    },
  })

  const viewCalls = useCallback((batchId) => {
    let queryString
    if (activeBatchIds?.includes(batchId)) {
      queryString = '?tab=active'
    }
    else {
      queryString = `?filter:batch_id:equals=${batchId}`
    }
    navigate(`/dashboard/call-logs${queryString}`)
  }, [activeBatchIds])

  const columns = useMemo(() => [
    {
      accessorKey: 'batch_status',
      cell: ({ getValue }) => {
        const statusValue = getValue()
        const status = typeof statusValue === 'string' ? statusMap[statusValue] : null
        return (
          <Chip
            variant="dot"
            radius="sm"
            color={status?.color ?? 'default'}
            className={`
              text-${status?.color ?? 'default'}
              uppercase
            `}
            classNames={{
              dot: 'animate-pulse',
            }}
          >
            {status?.label ?? 'unknown'}
          </Chip>
        )
      },
    },
    {
      accessorKey: 'description',
      size: 150,
      cell: ({ getValue }) => {
        const description = getValue()
        return (
          <h3 className={`
            truncate text-center font-taurus text-sm font-medium text-ellipsis
          `}
          >
            {description ?? 'Untitled Batch'}
          </h3>
        )
      },
    },
    {
      accessorKey: 'created_at',
      cell: ({ getValue }) => {
        const date = getValue()
        return date
          ? (
              <p className="text-center text-xs tracking-wider text-nowrap">
                {formatDistanceToNow(new Date(date), { addSuffix: true })}
              </p>
            )
          : '-'
      },
    },
    {
      accessorKey: 'id',
      size: 325,
      cell: ({ getValue }) => {
        const batchId = getValue()
        if (!batchId)
          return '-'
        return (
          <CopyButton
            value={batchId}
            displayText={batchId}
            color="default"
            className="font-mono text-gray-500"
          />
        )
      },
    },
    {
      id: 'actions',
      size: 100,
      cell: ({ row }) => {
        return (
          <ButtonGroup radius="none">
            <Tooltip content="View Call Logs">
              <Button
                isIconOnly
                variant="light"
                color="primary"
                onPress={() => viewCalls(row.original.id)}
              >
                <Rows4 size={16} />
              </Button>
            </Tooltip>
            <Tooltip content="Stop batch">
              <Button
                isDisabled={typeof row.original.batch_status !== 'string' || ['completed', 'failed'].includes(row.original.batch_status)}
                isIconOnly
                variant="light"
                color="danger"
                onPress={() => stopBatch.mutate(row.original.id)}
              >
                <OctagonX size={16} />
              </Button>
            </Tooltip>
          </ButtonGroup>
        )
      },
    },
  ], [activeBatchIds])

  // Setup TanStack Table
  const table = useReactTable({
    // STATE
    data: batches || [],
    columns,
    state: {
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    // SELECTION
    getRowId: row => row.id,
    onRowSelectionChange: setRowSelection,
  })

  return (
    <div className="p-7">
      <TopBarContent>
        <TopBar.Title>Batch Calls</TopBar.Title>
        <TopBar.Actions>
          <Button variant="primary" size="sm" onClick={() => navigate('/dashboard/create-batch')}>
            <Plus size={16} />
            Create Batch Call
          </Button>
        </TopBar.Actions>
      </TopBarContent>
      <div className="flex w-full flex-col gap-12 overflow-y-scroll pb-12">

        {isLoading
          ? (
              <div className="mt-40">
                <GradientLoadingAnimation
                  message="Loading Batches"
                  variant="roseGarden"
                  rounded="full"
                />
              </div>
            )
          : (!batches?.length
              ? (
                  <div className={`
                    mt-40 flex h-full w-full flex-col items-center gap-5
                  `}
                  >
                    <Rows4 size={64} className="text-gray-800" />
                    <h1 className={`
                      scroll-m-12 text-3xl font-semibold tracking-tighter
                    `}
                    >
                      No batches yet.
                    </h1>
                    <p className="text-sm text-gray-600">
                      Create a batch call to get started.
                    </p>
                    <Button
                      variant="solid"
                      color="primary"
                      radius="sm"
                      onPress={() => navigate('/dashboard/create-batch')}
                    >
                      <Plus size={16} />
                      Create Batch Call
                    </Button>
                  </div>
                )
              : (
                  <table className={`
                    mx-auto h-full w-full max-w-(--breakpoint-lg)
                  `}
                  >
                    <tbody className="flex w-full flex-col gap-4">
                      {table.getRowModel().rows.map(row => (
                        <Fragment key={row.id}>
                          <Tooltip radius="sm" placement="bottom" content={row.getIsSelected() ? 'Hide Logs' : 'Show Logs'}>
                            <tr
                              key={row.id}
                              onClick={row.getToggleSelectedHandler()}
                              className={cn(
                                `
                                  flex cursor-pointer items-center
                                  justify-between rounded-lg border-2
                                  border-gray-200 px-6 shadow-xs
                                  transition-colors
                                  hover:bg-gray-50
                                `,
                                row.getIsSelected() && 'bg-white shadow-md',
                              )}
                            >
                              {row.getVisibleCells().map((cell) => {
                                return (
                                  <td
                                    key={cell.id}
                                    style={{ width: cell.column.getSize() }}
                                    className="p-2"
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </td>
                                )
                              })}
                            </tr>
                          </Tooltip>
                          {rowSelection[row.id] && (
                            <tr>
                              <LogStream batchId={row.id} isLive={activeBatchIds?.includes(row.id)} />
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                    {hasNextPage && (
                      <tfoot>
                        <Button
                          color="default"
                          isLoading={isFetchingNextPage}
                          fullWidth
                          disableRipple
                          variant="bordered"
                          onPress={fetchNextPage}
                          className="mt-4"
                        >
                          Load More
                        </Button>
                      </tfoot>
                    )}
                  </table>
                )
            )}
      </div>
    </div>
  )
}
